/**
 * The Announcements list's filter + sort declaration — a consumer of the generic
 * filter toolkit (`components/filters`), mirroring `studentsFilterConfig` /
 * `withdrawalsFilterConfig`.
 *
 * Facets exposed on the bar:
 *   - search q   → `q`          debounced free text. Maps to the backend's `q`
 *                               param, a case-insensitive substring over the
 *                               announcement title OR body.
 *   - state      → `state`      multi-select over the DERIVED lifecycle states.
 *                               The table has no single status column — the
 *                               backend derives the state from the presence /
 *                               absence of `published_at` + `withdrawn_at`:
 *                                 draft     = never published, not withdrawn
 *                                 published = published, still live
 *                                 withdrawn = frozen (withdrawn_at set)
 *                               A `scheduled` state is intentionally NOT modelled
 *                               (it is a strict subset of draft — see the backend
 *                               `ANNOUNCEMENT_STATES` comment), so it is omitted
 *                               here to match. defaultValue [] = all states,
 *                               allEncoding 'omit'.
 *   - published  → `publishedFrom` / `publishedTo` date range over the
 *                               `published_at` timestamp. The backend pads these
 *                               date-only bounds to whole-day UTC server-side
 *                               (via `parseDateOnly`), so the toolkit's plain
 *                               `YYYY-MM-DD` output is correct as-is.
 *
 * Sorts map to the backend's allow-listed `sort` fields ({publishedAt,
 * createdAt}). The default (first entry) is "Recently published" =
 * publishedAt:desc — the list's pre-existing order.
 *
 * NOTE: the endpoint returns `{ items }` only — it is NOT paginated (it caps at
 * 50 rows server-side). The route reads `.items`, shows the count as
 * `items.length`, and renders NO Pagination control (there was none before).
 */

import { z } from 'zod';
import type { ListFilterConfig } from '../filters';

export const announcementsFilterConfig: ListFilterConfig = {
  facets: [
    {
      kind: 'search',
      key: 'q',
      label: 'Search',
      placeholder: 'Search announcements',
    },
    {
      kind: 'multi-select',
      key: 'state',
      label: 'State',
      group: 'filters',
      // No default constraint — a bare list shows every state. `omit` drops the
      // param entirely when nothing is picked, which the backend reads as "all".
      defaultValue: [],
      allEncoding: 'omit',
      options: [
        { value: 'draft', label: 'Draft' },
        { value: 'published', label: 'Published' },
        { value: 'withdrawn', label: 'Withdrawn' },
      ],
    },
    {
      kind: 'date-range',
      key: 'published',
      label: 'Published',
      group: 'filters',
      params: { from: 'publishedFrom', to: 'publishedTo' },
    },
  ],
  sorts: [
    { value: 'publishedAt:desc', label: 'Recently published' },
    { value: 'createdAt:desc', label: 'Recently created' },
  ],
  resultNoun: ['announcement', 'announcements'],
};

/**
 * URL search-param contract for the Announcements list, used as the route's
 * `validateSearch`. The filter toolkit is the single source of truth for these
 * values; this schema validates the raw URL at the route boundary (everything
 * optional, since a bare `/announcements` opens on the configured defaults).
 *
 * EVERY param key the config can emit must appear here — `z.object().parse`
 * strips unknown keys. `published` is the date-range facet's PRESET key (e.g.
 * `last7`), distinct from the concrete bounds `publishedFrom` / `publishedTo`.
 * There is no `page` key: the endpoint is not paginated.
 */
export const announcementsSearchSchema = z.object({
  q: z.string().optional(),
  state: z.string().optional(),
  published: z.string().optional(),
  publishedFrom: z.string().optional(),
  publishedTo: z.string().optional(),
  sort: z.string().optional(),
});
